import type { LoginMessageKey } from '$routes/(frontend)/login/types';
import { trim, upperFirst } from 'lodash-es';
import isEqual from 'lodash/isEqual';
import trimStart from 'lodash/trimStart';
import baseSlugify from 'slugify';
import { get } from 'svelte/store';
import { match, P } from 'ts-pattern';
import type { DpApHelper } from '$lib/DpAp/DpApHelper';
import type { CountryHelper } from '$lib/country/CountryHelper.js';
import { defaultLangForCountryOrNull } from '$lib/country/countryLang';
import { resolveRoute, resolveRouteByName } from '$lib/svelte/generated/resolveRoute';
import singleton from '$lib/utils/factory';
import type { zParticipantInput } from '$lib/utils/participantUtils';
import { safe } from '$lib/utils/string.js';
import { stripNullableValues } from '$lib/utils/stripNullableValues.js';
import type { lazy } from '$lib/utils/types';
import type { JsonObject } from '$lib/utils/zod';
import type { CurrentParticipantInput, ParticipantInput } from '$gql/graphql';
import { matchParticipantInput } from '$gql/resolvers/ParticipantRef';
import type { ServiceKey } from '$components/Navbar/services';
import {
    type SearchHeaderFormData,
    empty as SearchHeaderFormDataEmpty,
} from '$components/SearchHeader/SearchHeaderFormData';
import { matchWhereItem } from '$components/SearchHeader/utils';

export function slugify(string: string): string {
    return baseSlugify(string.replaceAll(/[’']/g, '-'), {
        lower: true,
        locale: 'fr',
        remove: /[^a-zA-Z0-9_’ -]/g,
    });
}

export function qs(params: URLSearchParams | null): string {
    const s = params?.toString();
    if (s == null || s === '') {
        return '';
    }
    return safe`?${s}`;
}

export type UrlCountry =
    | {
          type: 'UseCurrent';
      }
    | {
          type: 'null';
      }
    | string;

export const CountryUseCurrent: UrlCountry = { type: 'UseCurrent' as const } as const;
export const CountryNull: UrlCountry = { type: 'null' as const } as const;

type AdminParamsUrl =
    | { target: 'kibana/searchlog' }
    | {
          target: 'multiquote/list';
          filter?: { isValidate?: boolean | null };
      }
    | {
          target: 'review/list';
          filter?: { validate?: boolean | null };
      }
    | {
          target: 'user/list';
          filter?: {
              photoPendingValidation?: boolean | null;
              sortBy?: string | null;
              sortOrder?: string | null;
          };
      }
    | {
          target: 'user/edit';
          id: number;
      }
    | {
          target: 'company/list';
          filter?: {
              sortBy?: 'isClient' | null;
              sortOrder?: 'ASC' | 'DESC' | null;
              valid?: boolean | null;
              withManagers?: boolean | null;
              isProblematic?: boolean | null;
              country?: string | null;
              category?: number | null;
          };
      }
    | {
          target: 'company/edit';
          id: number;
      }
    | {
          target: 'goafricacontact/list';
          filter?: { isRead?: boolean | null };
      }
    | {
          target: 'cmscomment/list';
          filter?: { valid?: boolean | null };
      }
    | {
          target: 'inboxthread/list';
          filter?: { awaitingModeratorApproval?: boolean | null };
      }
    | {
          target: 'company-category-change-request/list';
          filter?: { approved?: true | false | 'pending' };
      }
    | {
          target: 'group/edit';
          id: number;
      };

export class UrlHelper {
    constructor(
        private readonly countryHelper: CountryHelper,
        private readonly goafricaDomain: string,
        private readonly dpapHelper: lazy<DpApHelper>,
        private readonly absoluteUrl: boolean,
        private readonly getLang: () => string,
    ) {}

    withAbsoluteUrl(this: this, absoluteUrl: boolean = true): UrlHelper {
        return new UrlHelper(
            this.countryHelper,
            this.goafricaDomain,
            this.dpapHelper,
            absoluteUrl,
            this.getLang,
        );
    }

    withLang(this: this, lang: string): UrlHelper {
        return new UrlHelper(
            this.countryHelper,
            this.goafricaDomain,
            this.dpapHelper,
            this.absoluteUrl,
            () => lang,
        );
    }

    homepage(
        this: this,
        { _country = CountryUseCurrent }: { _country?: UrlCountry },
    ): string {
        const lang = this.getLang();
        let resolvedCountry = this.resolveCountry(_country);

        if (resolvedCountry === null && lang === 'en') {
            return this.generateAbsoluteIfNeed('/en');
        } else {
            return this.generateCountryUrl('/', _country);
        }
    }

    company(this: this, company: { id: number; slug: string; country: string }): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/[_country]/[id]-[slug]' as const,
                params: {
                    _country: company.country.toLowerCase(),
                    id: safe`${company.id}`,
                    slug: company.slug,
                },
            }),
        );
    }

    newsfeed(
        this: this,
        { searchForm }: { searchForm?: Partial<SearchHeaderFormData> | null },
    ): string {
        if (searchForm != null) {
            return this.generateAbsoluteIfNeed(
                resolveRoute({
                    id: '/(frontend)/newsfeed',
                    params: {},
                    searchParams: { searchForm: JSON.stringify(searchForm) },
                }),
            );
        } else {
            return this.generateAbsoluteIfNeed(`/`);
        }
    }

    newsfeedBookmark(this: this, bookmarkId: number): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/newsfeed/bookmark/[bookmarkId=integer]',
                params: { bookmarkId: safe`${bookmarkId}` },
            }),
        );
    }

    newsfeedBookmarkAll(this: this): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/newsfeed/bookmark/all',
                params: {},
            }),
        );
    }

    newsfeedCategory(this: this, categoryId: number): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/newsfeed/category-posts/[categoryId=integer]',
                params: { categoryId: safe`${categoryId}` },
            }),
        );
    }

    newsfeedHashtag(this: this, hashtag: string): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/newsfeed/hashtag',
                params: {},
                searchParams: { tag: trim(trimStart(trim(hashtag), '#')) },
            }),
        );
    }

    newsfeedMyPendingPosts(this: this): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/newsfeed/my-pending-posts',
                params: {},
            }),
        );
    }

    categories(
        this: this,
        { _country = CountryUseCurrent }: { _country?: UrlCountry },
    ): string {
        const { country, lang } = this.resolveCountryAndLang(_country);

        return resolveRouteByName({
            name: 'directory',
            lang,
            params: { _country: country },
        });
    }

    explorer(
        this: this,
        { _country = CountryUseCurrent }: { _country?: UrlCountry },
    ): string {
        return this.generateCountryUrl('/explorer', _country);
    }

    category(
        this: this,
        {
            category,
            _country = CountryUseCurrent,
            page = 1,
        }: {
            _country?: UrlCountry;
            category: {
                slug: string;
            };
            page?: number;
        },
    ): string {
        const lang = this.getLang();

        const params = new URLSearchParams();
        if (page > 1) {
            params.set('p', page.toString());
        }

        if (lang === 'en') {
            return this.generateCountryUrl(
                safe`/directory/${category.slug}`,
                _country,
                params,
            );
        } else {
            return this.generateCountryUrl(
                safe`/annuaire/${category.slug}`,
                _country,
                params,
            );
        }
    }

    searchUsers(
        this: this,
        {
            searchForm,
            _country = CountryUseCurrent,
            page = 1,
        }: {
            _country?: UrlCountry;
            searchForm?: SearchHeaderFormData;
            page?: number;
        },
    ): string {
        const params = new URLSearchParams();
        if (page > 1) {
            params.set('page', page.toString());
        }

        if (
            searchForm != null &&
            !isEqual({ ...SearchHeaderFormDataEmpty, type: 'pro' as const }, searchForm)
        ) {
            params.set('searchForm', JSON.stringify(searchForm));
        }

        return this.generateCountryUrl(safe`/pros`, _country, params);
    }

    searchJobs(
        this: this,
        {
            searchForm,
            _country = CountryUseCurrent,
            page = 1,
        }: {
            _country?: UrlCountry;
            searchForm?: SearchHeaderFormData;
            page?: number | '__PAGE__';
        },
    ): string {
        const params = new URLSearchParams();
        if (page === '__PAGE__' || page > 1) {
            params.set('page', page.toString());
        }

        if (
            searchForm != null &&
            !isEqual({ ...SearchHeaderFormDataEmpty, type: 'job' as const }, searchForm)
        ) {
            params.set('searchForm', JSON.stringify(searchForm));
        }

        return this.generateCountryUrl(safe`/emploi`, _country, params);
    }

    jobCreate(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[[_country]]/emploi/publier',
                params: {},
            }),
        );
    }

    job(
        this: this,
        {
            country,
            jobId,
            jobSlug,
        }: {
            country: string;
            jobId: number;
            jobSlug: string;
        },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[[_country]]/emploi/job-[id]-[slug]',
                params: {
                    _country: country,
                    id: safe`${jobId}`,
                    slug: jobSlug,
                },
            }),
        );
    }

    jobTitleListing(
        this: this,
        { _country = CountryUseCurrent }: { _country?: UrlCountry },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[[_country]]/emploi/metiers',
                params: {
                    _country: this.resolveCountry(_country),
                },
            }),
        );
    }

    jobsJobTitle(
        this: this,
        {
            _country = CountryUseCurrent,
            jobTitleId,
            jobTitleSlug,
            page = 1,
        }: {
            _country?: UrlCountry;
            jobTitleId: number;
            jobTitleSlug: string;
            page?: number | '__PAGE__';
        },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[[_country]]/emploi/metier-[id]-[slug]',
                params: {
                    _country: this.resolveCountry(_country),
                    id: safe`${jobTitleId}`,
                    slug: jobTitleSlug,
                },
                searchParams: {
                    page: page === '__PAGE__' || page > 1 ? safe`${page}` : null,
                },
            }),
        );
    }

    multiQuotePage(
        this: this,
        {
            category = null,
            _country = CountryUseCurrent,
        }: {
            category: {
                id: number;
            } | null;
            _country?: UrlCountry;
        },
    ): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[[_country]]/devis',
                params: {
                    _country: this.resolveCountry(_country),
                },
                searchParams: {
                    category: category?.id,
                },
            }),
        );
    }

    contactUnsuspended(this: this, { id }: { id: number }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/contact/unsuspend/[companyId=integer]',
                params: {
                    companyId: safe`${id}`,
                },
            }),
        );
    }

    login(
        this: this,
        {
            targetPath,
            messageKey,
        }: {
            targetPath?: string | null;
            messageKey?: LoginMessageKey | null;
        } = {},
    ): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/login',
                params: {},
                searchParams: { target_path: targetPath, message_key: messageKey },
            }),
        );
    }

    registration(this: this): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription',
                params: {},
            }),
        );
    }

    logout(this: this): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/logout',
                params: {},
            }),
        );
    }

    contact(this: this): string {
        const lang = this.getLang();

        return this.generateAbsoluteIfNeed(
            resolveRouteByName({
                name: 'contact',
                lang,
                params: {},
            }),
        );
    }

    userProfile(
        this: this,
        user: {
            id: number;
            firstname: string | null;
            lastname: string | null;
            country: string | null;
        },
    ) {
        const { lastname, firstname, country } = user;
        const fullname =
            `${upperFirst(firstname ?? '')} ${upperFirst(lastname ?? '')}`.trim();

        let slug = slugify(fullname).trim();
        slug = slug === '' ? 'user' : slug;

        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[[_country]]/user/[userId=integer]-[[userSlug]]',
                params: {
                    _country: country,
                    userId: safe`${user.id}`,
                    userSlug: safe`${slug}`,
                },
            }),
        );
    }

    userAdminEditUrl(this: this, userId: number) {
        return this.generateAdminUrl({ target: 'user/edit', id: userId });
    }

    groupAdminEditUrl(this: this, groupId: number) {
        return this.generateAdminUrl({ target: 'group/edit', id: groupId });
    }

    companyAdminEditUrl(this: this, companyId: number) {
        return this.generateAdminUrl({ target: 'company/edit', id: companyId });
    }

    registrationStepConfirmation(
        this: this,
        {
            withCompany,
            email,
            userId,
            token,
        }: { withCompany: boolean; userId: number; email: string; token?: string },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/confirmation/[userId=integer]',
                params: {
                    userId: safe`${userId}`,
                },
                searchParams: {
                    with_company: withCompany ? '1' : null,
                    token,
                    email,
                },
            }),
        );
    }

    registrationStepInfosPerso(this: this, { withCompany }: { withCompany: boolean }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/infos-perso',
                params: {},
                searchParams: { with_company: withCompany ? '1' : null },
            }),
        );
    }

    registrationStepInfosPro(this: this, { withCompany }: { withCompany: boolean }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/infos-pro',
                params: {},
                searchParams: { with_company: withCompany ? '1' : null },
            }),
        );
    }

    registrationStepCompany(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/company',
                params: {},
            }),
        );
    }

    registrationStepCenterOfInterestCategories(
        this: this,
        { withCompany }: { withCompany: boolean },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/center-of-interest/categories',
                params: {},
                searchParams: { with_company: withCompany ? '1' : null },
            }),
        );
    }

    registrationStepCenterOfInterestCompanies(
        this: this,
        { withCompany }: { withCompany: boolean },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/center-of-interest/companies',
                params: {},
                searchParams: { with_company: withCompany ? '1' : null },
            }),
        );
    }

    registrationStepCenterOfInterestUsers(
        this: this,
        { withCompany }: { withCompany: boolean },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/step/center-of-interest/users',
                params: {},
                searchParams: { with_company: withCompany ? '1' : null },
            }),
        );
    }

    welcomePage(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inscription/bienvenue',
                params: {},
            }),
        );
    }

    inbox(
        this: this,
        {
            currentParticipant,
        }: { currentParticipant?: CurrentParticipantInput | null } = {},
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inbox',
                params: {},
                searchParams: {
                    currentParticipant:
                        currentParticipant != null
                            ? JSON.stringify(stripNullableValues(currentParticipant))
                            : null,
                },
            }),
        );
    }

    inboxError(this: this, error: JsonObject) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inbox/error',
                params: {},
                searchParams: {
                    error: JSON.stringify(error),
                },
            }),
        );
    }

    inboxCreateThread(
        this: this,
        {
            defaultParticipants = [],
            relatedJobOfferId = null,
        }: {
            defaultParticipants?: ParticipantInput[];
            relatedJobOfferId?: number | null;
        },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inbox/create-thread',
                params: {},
                searchParams: {
                    defaultParticipants:
                        defaultParticipants.length > 0
                            ? JSON.stringify(defaultParticipants)
                            : null,
                    relatedJobOfferId: relatedJobOfferId ?? null,
                },
            }),
        );
    }

    inboxThread(this: this, { threadId }: { threadId: number }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inbox/thread/[threadId]',
                params: { threadId: safe`${threadId}` },
                searchParams: {},
            }),
        );
    }

    inboxSwitchContext(
        this: this,
        {
            participant,
            target,
        }: { participant: zParticipantInput; target?: string | null },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/inbox/switch-context',
                params: {},
                searchParams: {
                    participantId: JSON.stringify(participant),
                    target: target ?? null,
                },
            }),
        );
    }

    adminDashboard(this: this) {
        return this.generateAbsoluteIfNeed('/admin/dashboard');
    }

    profileEdit(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/profile/edit',
                params: {},
            }),
        );
    }

    companyCreate(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/company/create',
                params: {},
            }),
        );
    }

    companyEditSelect(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/company/edit/select',
                params: {},
            }),
        );
    }

    resetPassword(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/registration/reset-password',
                params: {},
            }),
        );
    }

    notificationLink(this: this, notification: { id: number }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/notification/link/[id]',
                params: {
                    id: safe`${notification.id}`,
                },
            }),
        );
    }

    servicePage(this: this, serviceKey: ServiceKey): string {
        const lang = this.getLang();

        return this.generateAbsoluteIfNeed(
            match(serviceKey)
                .with('services_internet', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/services-internet',
                        params: {},
                    }),
                )
                .with('services_visibilite_digitale', () =>
                    resolveRouteByName({
                        name: 'service-page.visibilite_digitale',
                        lang,
                        params: {},
                    }),
                )
                .with('services_charte_verification_entreprise', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/charte-verification-entreprise',
                        params: {},
                    }),
                )
                .with('services_devis_centralises', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/devis-centralises',
                        params: {},
                    }),
                )
                .with('services_inbox', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/messagerie-instantannee',
                        params: {},
                    }),
                )
                .with('services_avis_clients', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/avis-clients',
                        params: {},
                    }),
                )
                .with('services_campagnes_emails', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/campagnes-emails-sms',
                        params: {},
                    }),
                )
                .with('services_afrikad', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/afrikad-monetisation-site-internet',
                        params: {},
                    }),
                )
                .with('services_shopping', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/produits-en-ligne',
                        params: {},
                    }),
                )
                .with('services_rdv_en_ligne', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/rendez-vous-en-ligne',
                        params: {},
                    }),
                )
                .with('services_reseau_social_pro', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/reseau-social-professionnel',
                        params: {},
                    }),
                )
                .with('services_land_entreprises_visibilite_digitale', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/(land)/land-entreprises-visibilite-digitale',
                        params: {},
                    }),
                )
                .with('services_land_membres_emploi', () =>
                    resolveRoute({
                        id: '/(frontend)/(service-page)/(with-nav)/(land)/land-membres-emploi',
                        params: {},
                    }),
                )
                .exhaustive(),
        );
    }

    notificationList(
        this: this,
        params: { isRead: boolean | null; page?: number } = {
            isRead: null,
            page: 1,
        },
    ) {
        const page = params.page ?? 1;
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/notifications',
                params: {},
                searchParams: {
                    page: page > 1 ? page : null,
                    isRead:
                        params.isRead === true
                            ? '1'
                            : params.isRead === false
                              ? '0'
                              : null,
                },
            }),
        );
    }

    staticPage(this: this, page: { slug: string }): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/page/[slug]',
                params: {
                    slug: page.slug,
                },
            }),
        );
    }

    mentionsLegales(this: this): string {
        return this.staticPage({ slug: 'mentions-legales' });
    }

    politiqueConfidentialite(this: this): string {
        return this.staticPage({ slug: 'politique-confidentialite' });
    }

    cgu(this: this) {
        return this.staticPage({ slug: 'cgu' });
    }

    socialLinks(this: this) {
        return {
            facebook: `https://www.facebook.com/goafricaonline`,
            twitter: `https://twitter.com/goafricaonline`,
            instagram: `https://www.instagram.com/goafricaonline`,
            linkedin: `https://www.linkedin.com/company/goafricaonline`,
            youtube: `https://www.youtube.com/channel/UCX1dbF2RbpUY4OAN42IzAeQ`,
            whatsapp: `https://whatsapp.com/channel/0029VanT2kE5vK9zqAwfmE0R`,
            tiktok: `https://www.tiktok.com/@goafricaonline`,
            blog: `https://blog.goafricaonline.com`,
        } as const;
    }

    feedPost(
        this: this,
        {
            author,
            authorSlug,
            feedPostId,
            comment,
        }: {
            author: ParticipantInput;
            authorSlug: string;
            feedPostId: number;
            comment?: number | null;
        },
    ): string {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/[participantId=participantIdWithPrefix]-[[participantSlug]]/feed/[id]',
                params: {
                    id: safe`${feedPostId}`,
                    participantSlug: slugify(authorSlug),
                    participantId: matchParticipantInput(author, {
                        user: (id) => `c${id}`,
                        company: (id) => `u${id}`,
                    }),
                },
                searchParams: {
                    comment: comment != null ? safe`${comment}` : null,
                },
            }),
        );
    }

    cmsCategory(
        this: this,
        {
            category,
            country,
        }: {
            category: { id: number; slug: string };
            country: UrlCountry;
        },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/[[_country]]/articles/cat/[id]-[slug]',
                params: {
                    _country: this.resolveCountry(country),
                    id: safe`${category.id}`,
                    slug: category.slug,
                },
            }),
        );
    }

    cmsPost(
        this: this,
        {
            post,
            country,
        }: {
            post: { id: number; slug: string; countries: string[]; onAfrica: boolean };
            country: UrlCountry;
        },
    ): string {
        const _country = this.resolveCountry(country);

        let hasCountry =
            _country == null
                ? post.onAfrica
                : post.countries.includes(_country.toUpperCase());

        let urlCountry = hasCountry ? _country : (post.countries.at(0) ?? null);

        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/[[_country]]/articles/cat/[id]-[slug]',
                params: {
                    _country: urlCountry,
                    id: safe`${post.id}`,
                    slug: post.slug,
                },
            }),
        );
    }

    newCompanies(this: this, { country = CountryUseCurrent }: { country?: UrlCountry }) {
        const _country = this.resolveCountry(country);
        const dp = this.dpapHelper().replaceDpAp(_country, '{dp}', 'fr');

        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/[[_country]]/nouvelles-societes-[slug]',
                params: {
                    _country: _country,
                    slug: _country == null ? 'afrique' : slugify(dp),
                },
            }),
        );
    }

    newComments(this: this, { country = CountryUseCurrent }: { country?: UrlCountry }) {
        const _country = this.resolveCountry(country);
        const dp = this.dpapHelper().replaceDpAp(_country, '{dp}', 'fr');

        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/[[_country]]/nouveaux-commentaires-[slug]',
                params: {
                    _country,
                    slug: _country == null ? 'afrique' : slugify(dp),
                },
            }),
        );
    }

    searchHeader(
        this: this,
        { searchForm, page = 1 }: { searchForm: SearchHeaderFormData; page?: number },
    ) {
        return match(searchForm.type)
            .with('company', () => {
                let whatWho = null;
                let where = null;

                if (
                    searchForm.whatWhoQuery != null &&
                    searchForm.whatWhoQuery.trim() !== ''
                ) {
                    whatWho = searchForm.whatWhoQuery;
                }

                if (searchForm.whereItem != null) {
                    where = matchWhereItem(searchForm.whereItem, {
                        city: (id) => safe`city-${id}`,
                        subCity: (id) => safe`subcity-${id}`,
                        district: (id) => safe`district-${id}`,
                        country: (id) => safe`country-${id}`,
                    });
                } else if (
                    searchForm.whereQuery != null &&
                    searchForm.whereQuery.trim() !== ''
                ) {
                    where = searchForm.whereQuery;
                }

                return this.generateAbsoluteIfNeed(
                    resolveRoute({
                        id: '/(symfony)/[[_country]]/annuaire-resultat',
                        params: {
                            _country: this.resolveCountry(CountryUseCurrent) ?? null,
                        },
                        searchParams: {
                            whatWho,
                            where,
                            type: 'company',
                        },
                    }),
                );
            })
            .with('pro', () => {
                return this.searchUsers({
                    page,
                    _country: CountryUseCurrent,
                    searchForm,
                });
            })
            .with('newsfeed', () => {
                return this.newsfeed({
                    searchForm,
                });
            })
            .with('job', () => {
                return this.searchJobs({
                    page,
                    _country: CountryUseCurrent,
                    searchForm,
                });
            })
            .exhaustive();
    }

    allReferences(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/entreprises-importantes-afrique',
                params: {},
            }),
        );
    }

    companyAdIframe({ adId, slot }: { adId: number; slot: string }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/company-ad/iframe/[adId]/[slot]',
                params: { adId: safe`${adId}`, slot },
            }),
        );
    }

    companyStaticAdIframe({ adId }: { adId: number }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/company-static-ad/iframe/[adId]',
                params: { adId: safe`${adId}` },
            }),
        );
    }

    companyAdClickRedirection({
        adId,
        slot,
        mediaId,
        requestInfo,
    }: {
        adId: number;
        slot: string;
        mediaId: number;
        requestInfo: string;
    }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/company-ad/click/[adId]/[slot]',
                params: { adId: safe`${adId}`, slot },
                searchParams: {
                    mediaId: safe`${mediaId}`,
                    request_info: requestInfo,
                },
            }),
        );
    }

    companyStaticAdClickRedirection(
        this: this,
        {
            adId,
            requestInfo,
        }: {
            adId: number;
            requestInfo: string;
        },
    ) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/company-static-ad/click/[adId]',
                params: { adId: safe`${adId}` },
                searchParams: {
                    request_info: requestInfo,
                },
            }),
        );
    }

    whoAreWe(this: this) {
        return this.staticPage({ slug: 'equipe-go-africa' });
    }

    ajaxResumeImage(this: this, resumeId: number) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(symfony)/ajax/file/resume-image/[resumeId]',
                params: { resumeId: safe`${resumeId}` },
            }),
        );
    }

    newsfeedAuthor(this: this, author: ParticipantInput): string {
        return this.newsfeed({
            searchForm: {
                type: 'newsfeed',
                whatWhoItem: matchParticipantInput(author, {
                    user: (id) => ({ userId: id }),
                    company: (id) => ({ companyId: id }),
                }),
            },
        });
    }

    _editProfile = singleton(
        () =>
            ({
                profile: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({ id: '/(symfony)/profile/edit', params: {} }),
                    ),
                photos: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/photos',
                            params: {},
                        }),
                    ),
                password: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/password',
                            params: {},
                        }),
                    ),
                changeEmail: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/change-email',
                            params: {},
                        }),
                    ),
                formations: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/formations',
                            params: {},
                        }),
                    ),
                experiences: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/experiences',
                            params: {},
                        }),
                    ),
                languages: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/languages',
                            params: {},
                        }),
                    ),
                interests: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/interests',
                            params: {},
                        }),
                    ),
                coordinates: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/coordinates',
                            params: {},
                        }),
                    ),
                resumes: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(symfony)/profile/edit/resumes',
                            params: {},
                        }),
                    ),
                pro: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({ id: '/(symfony)/profile/edit/pro', params: {} }),
                    ),
                portfolio: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(frontend)/profile/edit/portfolio',
                            params: {},
                        }),
                    ),
                notificationSettings: () =>
                    this.generateAbsoluteIfNeed(
                        resolveRoute({
                            id: '/(frontend)/profile/edit/notification-settings',
                            params: {},
                        }),
                    ),
            }) as const,
    );

    editProfile(this: this) {
        return this._editProfile();
    }

    editCompanyProfile(
        this: this,
        {
            companyId,
            companySlug,
            path,
        }: {
            companyId: number;
            companySlug: string;
            path:
                | 'infos'
                | 'coordinates'
                | 'category'
                | 'descriptions'
                | 'planning'
                | 'job_offers'
                | 'profile_management'
                | 'shopping'
                | 'managers'
                | 'medias'
                | 'photos';
        },
    ) {
        if (path === 'infos') {
            return this.generateAbsoluteIfNeed(
                `/company/${companyId}-${companySlug}/edit`,
            );
        } else {
            return this.generateAbsoluteIfNeed(
                `/company/${companyId}-${companySlug}/edit/${path}`,
            );
        }
    }

    nextProfiler(this: this) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/(profiler)/_next-profiler',
                params: {},
            }),
        );
    }

    nextProfilerRequest(this: this, args: { requestId: string }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/(profiler)/_next-profiler/request-[id]',
                params: { id: safe`${args.requestId}` },
            }),
        );
    }

    nextProfilerRequestElastic(this: this, args: { requestId: string }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/(profiler)/_next-profiler/request-[id]/elastic',
                params: { id: safe`${args.requestId}` },
            }),
        );
    }

    nextProfilerRequestLog(this: this, args: { requestId: string }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/(profiler)/_next-profiler/request-[id]/log',
                params: { id: safe`${args.requestId}` },
            }),
        );
    }

    nextProfilerRequestSql(this: this, args: { requestId: string }) {
        return this.generateAbsoluteIfNeed(
            resolveRoute({
                id: '/(frontend)/(profiler)/_next-profiler/request-[id]/log',
                params: { id: safe`${args.requestId}` },
            }),
        );
    }

    resolveCountry(this: this, country: UrlCountry): string | null {
        return get(this.countryHelper.resolveCountry(country));
    }

    resolveCountryAndLang(
        this: this,
        country: UrlCountry,
    ): { country: string | null; lang: string } {
        const resolvedCountry = this.resolveCountry(country);
        const defaultLang = this.getLang();
        const countryLang =
            resolvedCountry == null ? null : defaultLangForCountryOrNull(resolvedCountry);

        const lang = countryLang ?? defaultLang;

        return { country: resolvedCountry, lang };
    }

    switchUserExit(this: this) {
        const params = new URLSearchParams();
        params.set('_switch_user', '_exit');

        return this.generateAbsoluteIfNeed(`/admin/dashboard${qs(params)}`);
    }

    generateAdminUrl(params: AdminParamsUrl) {
        const searchParams = new URLSearchParams();

        return match(params)
            .with({ target: 'kibana/searchlog' }, () => {
                return this.generateAbsoluteIfNeed(
                    `/admin/kibana/searchlog${qs(searchParams)}`,
                );
            })
            .with(
                { target: 'multiquote/list', filter: P.optional(P.select()) },
                (filter) => {
                    if (filter?.isValidate != null) {
                        searchParams.set(
                            'filter[isValidate][value]',
                            filter.isValidate ? 'yes' : 'no',
                        );
                    }

                    return this.generateAbsoluteIfNeed(
                        `/admin/netosoft/goafrica/multiquote/list${qs(searchParams)}`,
                    );
                },
            )
            .with({ target: 'user/list', filter: P.optional(P.select()) }, (filter) => {
                if (filter?.photoPendingValidation != null) {
                    searchParams.set(
                        'filter[photoPendingValidation][value]',
                        filter.photoPendingValidation ? '1' : '0',
                    );
                }

                if (filter?.sortBy != null) {
                    searchParams.set('filter[_sort_by]', filter.sortBy);
                }

                if (filter?.sortOrder != null) {
                    searchParams.set('filter[_sort_order]', filter.sortOrder);
                }

                return this.generateAbsoluteIfNeed(
                    `/admin/netosoft/goafrica/user/list${qs(searchParams)}`,
                );
            })
            .with({ target: 'user/edit', id: P.select() }, (id) => {
                return this.generateAbsoluteIfNeed(
                    safe`/admin/netosoft/goafrica/user/${id}/edit`,
                );
            })
            .with(
                { target: 'company/list', filter: P.optional(P.select()) },
                (filter) => {
                    if (filter?.sortBy != null) {
                        searchParams.set('filter[_sort_by]', filter.sortBy);
                    }
                    if (filter?.sortOrder != null) {
                        searchParams.set('filter[_sort_order]', filter.sortOrder);
                    }
                    if (filter?.valid != null) {
                        searchParams.set(
                            'filter[valid][value]',
                            filter.valid ? '1' : '2',
                        );
                    }
                    if (filter?.withManagers != null) {
                        searchParams.set(
                            'filter[withManagers][value]',
                            filter.withManagers ? 'yes' : 'no',
                        );
                    }
                    if (filter?.isProblematic != null) {
                        searchParams.set('filter[isProblematic][type]', '');
                        searchParams.set(
                            'filter[isProblematic][value]',
                            filter.isProblematic ? '1' : '0',
                        );
                    }
                    if (filter?.country != null) {
                        searchParams.set('filter[country][value]', filter.country);
                    }
                    if (filter?.category != null) {
                        searchParams.set(
                            'filter[categories][value][categories]',
                            safe`${filter.category}`,
                        );
                        searchParams.set('filter[categories][value][inversed]', '0');
                    }
                    return this.generateAbsoluteIfNeed(
                        `/admin/netosoft/goafrica/company/list${qs(searchParams)}`,
                    );
                },
            )
            .with({ target: 'company/edit', id: P.select() }, (id) => {
                return this.generateAbsoluteIfNeed(
                    safe`/admin/netosoft/goafrica/company/${id}/edit`,
                );
            })
            .with({ target: 'review/list', filter: P.optional(P.select()) }, (filter) => {
                if (filter?.validate != null) {
                    searchParams.set(
                        'filter[validate][value]',
                        filter.validate ? '1' : '2',
                    );
                }

                return this.generateAbsoluteIfNeed(
                    `/admin/netosoft/goafrica/review/list${qs(searchParams)}`,
                );
            })
            .with(
                { target: 'goafricacontact/list', filter: P.optional(P.select()) },
                (filter) => {
                    if (filter?.isRead != null) {
                        searchParams.set(
                            'filter[isRead][value]',
                            filter.isRead ? 'yes' : 'no',
                        );
                    }

                    return this.generateAbsoluteIfNeed(
                        `/admin/netosoft/goafrica/goafricacontact/list${qs(searchParams)}`,
                    );
                },
            )
            .with(
                { target: 'cmscomment/list', filter: P.optional(P.select()) },
                (filter) => {
                    if (filter?.valid != null) {
                        searchParams.set(
                            'filter[valid][value]',
                            filter.valid ? 'yes' : 'no',
                        );
                    }

                    return this.generateAbsoluteIfNeed(
                        `/admin/netosoft/goafrica/cms-cmscomment/list${qs(searchParams)}`,
                    );
                },
            )
            .with(
                { target: 'inboxthread/list', filter: P.optional(P.select()) },
                (filter) => {
                    if (filter?.awaitingModeratorApproval != null) {
                        searchParams.set(
                            'filter[awaitingModeratorApproval][value]',
                            filter.awaitingModeratorApproval ? 'yes' : 'no',
                        );
                    }

                    return this.generateAbsoluteIfNeed(
                        `/admin/netosoft/goafrica/inbox-inboxthread/list${qs(searchParams)}`,
                    );
                },
            )
            .with(
                {
                    target: 'company-category-change-request/list',
                    filter: P.optional(P.select()),
                },
                (filter) => {
                    if (filter?.approved != null) {
                        searchParams.set(
                            'filter[approved][value]',
                            match(filter.approved)
                                .with(true, () => 'yes')
                                .with(false, () => 'no')
                                .with('pending', () => 'pending')
                                .exhaustive(),
                        );
                    }

                    return this.generateAbsoluteIfNeed(
                        `/admin/netosoft/goafrica/companycategorychangerequest/list${qs(searchParams)}`,
                    );
                },
            )
            .with({ target: 'group/edit', id: P.select() }, (id) => {
                return this.generateAbsoluteIfNeed(
                    safe`/admin/netosoft/goafrica/group/${id}/edit`,
                );
            })
            .exhaustive();
    }

    generateCountryUrl(
        this: this,
        basePath: string,
        country: UrlCountry,
        searchParams: URLSearchParams | null = null,
    ): string {
        let _country = this.resolveCountry(country);

        if (_country === null) {
            return this.generateAbsoluteIfNeed(
                safe`/${trim(basePath, '/')}${qs(searchParams)}`,
            );
        } else {
            return this.generateAbsoluteIfNeed(
                safe`/${trim(safe`${_country.toLowerCase()}${basePath}`, '/')}${qs(searchParams)}`,
            );
        }
    }

    generateAbsoluteIfNeed(
        this: this,
        url: string,
        options: {
            subdomain: string;
            protocol: 'http' | 'https';
        } = { subdomain: 'www', protocol: 'https' },
    ): string {
        if (!this.absoluteUrl) {
            return url;
        }

        const prefix = safe`${options.protocol}://${options.subdomain}`;
        if (url.startsWith(prefix)) {
            return url;
        }

        return safe`${options.protocol}://${options.subdomain}.${this.goafricaDomain}${url}`;
    }
}
